import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Tag as TagIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { TAG_COLOR_KEYS, getTagColor } from "@/lib/constants";
import { TagSwatch } from "./TagBadge";
import { createTag, listTags } from "@/api/tags";
import type { Tag, TagWithCount } from "@/api/types";

interface TagSelectorProps {
  projectSlug: string;
  /** Tags already attached to the company; hidden from the picker. */
  excludeIds: number[];
  /** Called with the picked (existing or freshly created) tag id. */
  onPick: (tagId: number) => Promise<void> | void;
  /** Optional seed list — if provided, the picker uses it instead of fetching. */
  seedTags?: TagWithCount[];
}

type Mode = "search" | "create";

export function TagSelector({
  projectSlug,
  excludeIds,
  onPick,
  seedTags,
}: TagSelectorProps) {
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState<TagWithCount[]>(seedTags ?? []);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<Mode>("search");
  const [newColor, setNewColor] = useState<string>("indigo");
  const [submitting, setSubmitting] = useState(false);
  const [picking, setPicking] = useState<number | null>(null);

  // Reset on close.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setMode("search");
      setNewColor("indigo");
      setSubmitting(false);
      setPicking(null);
    }
  }, [open]);

  // Fetch tags when opened (skip if seedTags is provided).
  useEffect(() => {
    if (!open || seedTags) return;
    let cancelled = false;
    setLoading(true);
    listTags(projectSlug)
      .then((res) => !cancelled && setTags(res))
      .catch(() => !cancelled && setTags([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, projectSlug, seedTags]);

  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tags
      .filter((t) => !excluded.has(t.id))
      .filter((t) => !q || t.name.toLowerCase().includes(q));
  }, [tags, excluded, search]);

  const exactMatch = useMemo(
    () =>
      tags.some(
        (t) => t.name.toLowerCase() === search.trim().toLowerCase(),
      ),
    [tags, search],
  );
  const showCreatePrompt =
    mode === "search" && search.trim().length > 0 && !exactMatch;

  async function pickExisting(tagId: number) {
    setPicking(tagId);
    try {
      await onPick(tagId);
      setOpen(false);
    } finally {
      setPicking(null);
    }
  }

  async function submitNew() {
    const name = search.trim();
    if (!name) return;
    setSubmitting(true);
    try {
      const created = await createTag(projectSlug, { name, color: newColor });
      // Mirror the new tag locally so the next open of the picker sees it
      // even before a parent refresh.
      setTags((prev) => [...prev, { ...created, company_count: 0 }]);
      await onPick(created.id);
      setOpen(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="text-slate-500">
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add tag
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[300px] p-0">
        {mode === "search" ? (
          <Command>
            <CommandInput
              placeholder="Search or create a tag…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center gap-2 px-3 py-3 text-xs text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading tags…
                </div>
              ) : matches.length === 0 && !showCreatePrompt ? (
                <CommandEmpty>No tags yet.</CommandEmpty>
              ) : null}

              {matches.length > 0 && (
                <CommandGroup heading="Tags">
                  {matches.map((t) => {
                    const colors = getTagColor(t.color);
                    return (
                      <CommandItem
                        key={t.id}
                        value={t.name}
                        onSelect={() => void pickExisting(t.id)}
                        disabled={picking !== null}
                      >
                        <span
                          className={cn(
                            "mr-2 h-2 w-2 shrink-0 rounded-full",
                            colors.dot,
                          )}
                        />
                        <span className="flex-1 truncate">{t.name}</span>
                        {picking === t.id && (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}

              {showCreatePrompt && (
                <CommandGroup heading="New">
                  <CommandItem
                    value={`__create__${search}`}
                    onSelect={() => setMode("create")}
                  >
                    <TagIcon className="mr-2 h-3.5 w-3.5 text-slate-400" />
                    Create &ldquo;{search.trim()}&rdquo;
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        ) : (
          <div className="space-y-3 p-3">
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Name
              </label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                className="h-9 rounded-lg border-slate-200"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Color
              </label>
              <div className="grid grid-cols-9 gap-1.5">
                {TAG_COLOR_KEYS.map((c) => (
                  <TagSwatch
                    key={c}
                    color={c}
                    selected={c === newColor}
                    onClick={() => setNewColor(c)}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-between gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setMode("search")}
                disabled={submitting}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => void submitNew()}
                disabled={submitting || search.trim().length === 0}
              >
                {submitting && (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                )}
                Create &amp; add
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 409) {
      return "A tag with this name already exists.";
    }
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't create the tag.";
}

// Re-export Tag type for callers that just want this barrel.
export type { Tag };
